import { Injectable, inject } from "@angular/core";
import { Params } from "@angular/router";

import { Observable } from "rxjs";
import { ConditionalKeys, Entries, Merge, Tagged } from "type-fest";

import { ControllerBase } from "./controller-bare.service";

export type LogicalPage = Tagged<number, "LogicalPage">;
export type PhysicalPage = Tagged<number, "PhysicalPage">;
export type PdfOffset = Tagged<number, "PdfOffset">;

@Injectable({ providedIn: "root" })
export class BlobService {
  private readonly base = inject(ControllerBase);

  downloadBlobUrl(container: string, blobName: string) {
    /** TODO: check query */
    return `${this.base.apiUrl}/blob/${container}/${blobName}/download`;
  }
}

@Injectable({ providedIn: "root" })
export class HierarchyService {
  private readonly base = inject(ControllerBase);

  /** OK */
  getPathFromRoot(targetId: string, rootId: string, fillSiblings: boolean) {
    return this.base.handled.get<HierarchyNodeViewModel[]>(`hierarchy/${targetId}/getPathFrom/${rootId}/${fillSiblings}`);
  }

  /** OK */
  getNode(id: string) {
    return this.base.handled.get<HierarchyNodeViewModel>(`hierarchy/${id}`);
  }
}

@Injectable({ providedIn: "root" })
export class HomeService {
  private readonly base = inject(ControllerBase);

  getNavigationRoot(): Observable<HierarchyNodeViewModel> {
    return this.base.get<HierarchyNodeViewModel>(`home/getNavigationRoot`, { queryDepth: "2" });
  }
}

@Injectable({ providedIn: "root" })
export class EditorConroller {
  private readonly base = inject(ControllerBase);

  save(id: NodeId, model: SaveViewModel, sessionId: SessionId, skipReindexing: boolean) {
    /* SHOULD PASS THROUGH ERRORS */
    return this.base.post<EditorViewModel>(`editor/${id}/save?sessionId=${sessionId}&skipReindexing=${skipReindexing}`, model);
  }

  load(id: NodeId) {
    /* SHOULD PASS THROUGH ERRORS */
    return this.base.get<EditorViewModel>(`editor/${id}/load`);
  }
}

@Injectable({ providedIn: "root" })
export class NodeService {
  private readonly base = inject(ControllerBase);

  /* CURRENTLY NOT USED */
  getNodeNavigationAxes(id: string) {
    return this.base.get<NodeNavigationViewModel>(`hierarchy/${id}/navigation`);
  }

  /** OK */
  remove(id: string, sessionId: string, force: boolean) {
    /* SHOULD PASS THROUGH ERRORS */
    return this.base.post<void>(`node/${id}/remove/${sessionId}/${force}`);
  }

  /** OK */
  tryGetLock(id: string, sessionId: string, force: boolean) {
    /* SHOULD PASS THROUGH ERRORS */
    return this.base.get<Lock>(`node/${id}/tryGetLock/${sessionId}/${force}`);
  }

  /** OK */
  tryUnlock(id: string, force: boolean) {
    /* SHOULD PASS THROUGH ERRORS */
    return this.base.get<boolean>(`node/${id}/tryUnlock/${force}`);
  }

  moveMany(ids: string[], sessionId: string, position: InsertNodePosition, anchorId: string) {
    return this.base.post<void>(`node/${anchorId}/moveMany/${position}?sessionId=${sessionId}`, { ids });
  }

  create(nodeName: string, title: string, sessionId: string, position: InsertNodePosition, anchorId: string) {
    return this.base.post<HierarchyNodeViewModel>(`node/${anchorId}/create/${position}?sessionId=${sessionId}`, { nodeName, title });
  }
}

@Injectable({ providedIn: "root" })
export class QueryController {
  private readonly base = inject(ControllerBase);

  /** OK */
  autocomplete(autocompleteRequest: AutocompleteRequest) {
    return this.base.post<AutocompleteSuggestion[]>(`query/autocomplete`, autocompleteRequest);
  }

  /** OK */
  executeQuery(query: QueryViewModel) {
    return this.base.post<QueryResult>(`query/`, query);
  }
}

@Injectable({ providedIn: "root" })
export class UserService {
  private readonly base = inject(ControllerBase);

  /** OK */
  getCurrentUser() {
    return this.base.get<UserAccount | null>(`user/current`);
  }
}

export enum ResourceRole {
  Display = "display",
  Cover = "cover",
  Download = "download",
}

export interface Resource {
  role: ResourceRole;
  uri?: string;
}

export interface MetaTags {
  keywords?: string | null;
  description?: string | null;
  title?: string | null;
}

export interface EditorDocumentViewModel extends NodeViewModelBase {
  pdfUri: string | null | undefined;

  pageCount: PhysicalPage | null | undefined;
  pdfOffset: PdfOffset | null | undefined;
  periodEnd: string | null | undefined;
  periodStart: string | null | undefined;
  sessionMonth: string | null | undefined;
  citationReference: string | null | undefined;
  resources: Record<ResourceType, string> | null | undefined;
}

export interface EditorChapterViewModel extends NodeViewModelBase {
  startPage: PhysicalPage | null | undefined;
  endPage: PhysicalPage | null | undefined;
  topics: string[] | null | undefined;
}

interface NodeViewModelBase {
  id: NodeId;
  nodeName: SchemaLevel;
  version?: NodeVersion | null;
  title?: string;
  content?: string | null;
  hidden?: boolean | null;
  metaTags?: MetaTags | null;
}

export interface EditorViewModel extends NodeViewModelBase {
  publication?: HierarchyNodeViewModel;
  document?: EditorDocumentViewModel;
  chapter?: ChapterObject;
  organization?: OrganizationId | null;
}

export interface SaveDocumentObjectViewModel {
  id: string;
  pageCount: number | null | undefined;
  pdfOffset: PdfOffset | null | undefined;
  periodEnd: string | null | undefined;
  periodStart: string | null | undefined;
  sessionMonth: string | null | undefined;
  citationReference: string | null | undefined;
}

export interface SaveChapterViewModel {
  id: string;
  startPage: LogicalPage | null | undefined;
  topics: string[] | null | undefined;
}

export interface SaveViewModel {
  title: string | null | undefined;
  content: string | null | undefined;
  hidden: boolean | undefined;
  document: SaveDocumentObjectViewModel | null | undefined;
  chapter: SaveChapterViewModel | null | undefined;
  metaTags: MetaTags | null | undefined;
}

export interface ExtendedHierarchyNode extends HierarchyNodeViewModel {
  path: string[];
  children: ExtendedHierarchyNode[] | null;
  organization: OrganizationId | null;
}

export type PublicationNode = Merge<ExtendedHierarchyNode, { id: PublicationType }>;

/****************************************************************/

interface HierarchyNodeViewModelBase {
  id: NodeId;
  nodeName: SchemaLevel;
  title: string;
  content?: string | null;
  metaTags?: MetaTags | null;
  hidden: boolean;
  //
  hasChildren: boolean;
  children: HierarchyNodeViewModel[] | null;
  icon?: string | null;
  resources?: Record<ResourceType, string> | null;
  queryFields?: QueryFieldViewModel[] | null;
  startPage: LogicalPage;
  endPage: LogicalPage;
}

export interface HierarchyNodeViewModel extends ChapterHierarchyNodeViewModel, DocumentHierarchyNodeViewModel { }

export interface ChapterHierarchyNodeViewModel extends HierarchyNodeViewModelBase { }

export interface DocumentHierarchyNodeViewModel extends HierarchyNodeViewModelBase {
  documentObject?: DocumentObject;
}

/****************************************************************/

export namespace NodeVM {
  export function isLevel(node: NodeViewModelBase): boolean {
    // Legacy XML compatibilty and catch all
    return !isWebPage(node) && !isDocument(node) && !isChapter(node) && !isOrganization(node) && !isPublication(node);
  }
  export function isWebPage(node: NodeViewModelBase): boolean {
    return node.nodeName === SchemaLevel.WebPage;
  }
  export function isDocument(node: NodeViewModelBase): boolean {
    return node.nodeName === SchemaLevel.Document;
  }
  export function isChapter(node: NodeViewModelBase): boolean {
    return node.nodeName === SchemaLevel.Chapter;
  }
  export function isOrganization(node: NodeViewModelBase): boolean {
    return node.nodeName === SchemaLevel.Organization;
  }
  export function isPublication(node: NodeViewModelBase): node is PublicationNode {
    return node.nodeName === SchemaLevel.Publication;
  }
}

export interface NodeNavigationViewModel {
  parentId: string;
  parentTitle: string;
  parentNodeName: string;
}

export enum InsertNodePosition {
  AsFirstChild = "AsFirstChild",
  AsLastChild = "AsLastChild",
  Before = "Before",
  After = "After",
}

export interface Lock {
  nodeId: NodeId;
  nodeVersion: number;
  nodeLastChangedTimestamp: number | null;
  nodeLastChangedUser: string;
  timestamp: number;
  lockingUser: string;
  sessionId: string;
}

export interface AutocompleteSuggestion {
  queryPart: QueryTerm;
  matchSegments: string[];
  id: string;
}

export interface AutocompleteRequest {
  queryTermTypes: QueryTermType[];
  input: string;
  limit: number;
}

export interface QueryResult {
  entries: EntryResult[];
  length?: number;
}

export interface QueryViewModel {
  parts?: QueryTerm[] | null;
  skip?: number | null;
  take?: number | null;
  withCount?: boolean | null;
}

export interface UserAccount {
  id: string;
  roles: string[];
  userName: string;
}

export interface HierarchyNodeLinkViewModel {
  nodeId: string;
  linkType: HierarchyNodeLinkType;
}

export interface QueryTerm {
  type: QueryTermType;
  value: string;
  caption?: string;
}

export enum QueryTermType {
  Title = "Title",
  Name = "Name",
  IssueNumber = "IssueNumber",
  Year = "Year",
  PeriodStart = "PeriodStart",
  PeriodEnd = "PeriodEnd",
  SessionMonth = "SessionMonth",
  Persons = "Persons",
  Topics = "Topics",
  CaseNumbers = "CaseNumbers",
  PageNumber = "PageNumber",
  CitationReference = "CitationReference",
  Id = "Id",
  ExternalId = "ExternalId",
  State = "State",
  Language = "Language",
  DocumentType = "DocumentType",
  OrganizationId = "OrganizationId",
  Fulltext = "Fulltext",
}

export namespace ShortQueryTermType {
  export function toShort(value: Partial<Record<QueryTermType, any>> | null | undefined) {
    return !value ? value : Object.fromEntries((Object.entries(value) as Entries<typeof value>).map(([k, v]) => [fromQueryTermType[k], v]));
  }
  export function fromShort(value: Params): Partial<Record<QueryTermType, any>> {
    return !value
      ? value
      : Object.fromEntries(
        (Object.entries(value) as Entries<typeof value>).filter(([k, _]) => k in toQueryTermType).map(([k, v]) => [toQueryTermType[k], v])
      );
  }

  type ShortQueryTermType = (typeof fromQueryTermType)[QueryTermType];

  const fromQueryTermType = {
    [QueryTermType.Title]: "ti",
    [QueryTermType.Name]: "n",
    [QueryTermType.IssueNumber]: "in",
    [QueryTermType.Year]: "y",
    [QueryTermType.PeriodStart]: "ps",
    [QueryTermType.PeriodEnd]: "pe",
    [QueryTermType.SessionMonth]: "sm",
    [QueryTermType.Persons]: "p",
    [QueryTermType.Topics]: "to",
    [QueryTermType.CaseNumbers]: "cn",
    [QueryTermType.PageNumber]: "pn",
    [QueryTermType.CitationReference]: "cr",
    [QueryTermType.Id]: "id",
    [QueryTermType.ExternalId]: "eid",
    [QueryTermType.State]: "s",
    [QueryTermType.Language]: "l",
    [QueryTermType.DocumentType]: "dt",
    [QueryTermType.OrganizationId]: "oid",
    [QueryTermType.Fulltext]: "search",
  } as const satisfies Record<QueryTermType, String>;

  const toQueryTermType = {
    ti: QueryTermType.Title,
    n: QueryTermType.Name,
    in: QueryTermType.IssueNumber,
    y: QueryTermType.Year,
    ps: QueryTermType.PeriodStart,
    pe: QueryTermType.PeriodEnd,
    sm: QueryTermType.SessionMonth,
    p: QueryTermType.Persons,
    to: QueryTermType.Topics,
    cn: QueryTermType.CaseNumbers,
    pn: QueryTermType.PageNumber,
    cr: QueryTermType.CitationReference,
    id: QueryTermType.Id,
    eid: QueryTermType.ExternalId,
    s: QueryTermType.State,
    l: QueryTermType.Language,
    dt: QueryTermType.DocumentType,
    oid: QueryTermType.OrganizationId,
    search: QueryTermType.Fulltext,
  } as const satisfies { [P in ShortQueryTermType]: ConditionalKeys<typeof fromQueryTermType, P> };
}

export interface DocumentObject {
  title: string | null | undefined;
  pageCount: PhysicalPage | null | undefined;
  pdfOffset: PdfOffset | null | undefined;
  externalId: string | null | undefined;
  issueNumber: string | null | undefined;
  year: string | null | undefined;
  periodStart: string | null | undefined;
  periodEnd: string | null | undefined;
  sessionMonth: string | null | undefined;
  citationReference: string | null | undefined;
}

export interface ChapterObject {
  title: string | null | undefined;
  startPage: LogicalPage | null | undefined;
  endPage: LogicalPage | null | undefined;
  topics: string[] | null | undefined;
}

export interface EntryResult {
  id: NodeId;
  // documentId: DocumentId;
  // documentType: PublicationType;
  // documentCover: string;
  page: number | null;
  // pageCount: number | null;
  // periodStart: string;
  // periodEnd: string;
  // year: number | null;
  title: string;
  content?: string;
  objects?: ObjectResult[];
  coverUri?: string;
}

export enum QueryDepth {
  Full = "Full",
  Single = "Single",
  SingleWithHierarchyAttributes = "SingleWithHierarchyAttributes",
  Parent = "Parent",
  Metadata = "Metadata",
  FullWithParentId = "FullWithParentId",
}

export enum HierarchyNodeLinkType {
  Child = "Child",
  Parent = "Parent",
}

export interface ObjectResult {
  id: number;
  logicalPageNumber: LogicalPage;
  fulltextSnippet: string;
  chapterName: string;
}

const tag: unique symbol = undefined!;
// type Tagged<Token> = { readonly [tag]: Token };
// type Tagged<Type, Token = unknown> = Type & Tagged<Token>;

export type NodeId = Tagged<string, "NodeId">;
export type DocumentId = Tagged<NodeId, "DocumentId">;
export type PublicationType = Tagged<NodeId, "PublicationType">;
export type OrganizationId = Tagged<NodeId, "Organization">;
export type Role = Tagged<string, "Role">;
export type NodeVersion = Tagged<number, "NodeVersion">;
export type SessionId = Tagged<string, "SessionId">;

export type ResourceType = "cover" | "display" | "download";

export type QueryFieldType = "input" | "slider" | "select";
export type QueryInputType = "text" | "number";
export type QueryFieldWidth = "half" | "full";

export interface QueryFieldViewModel {
  term: QueryTermType;
  label: String;
  fieldType: QueryFieldType;
  inputType?: QueryInputType | null;
  width?: QueryFieldWidth | null;
  multi?: Boolean | null;
  min?: number | null;
  max?: number | null;
  offset?: number | null;
  defaultValues?: string[] | null;
}

export enum SchemaLevel {
  Archive = "archive",
  WebPages = "web-pages",
  WebPage = "web-page",
  Organization = "organization",
  Publication = "publication",
  Level = "level",
  Document = "document",
  Chapter = "chapter",
}
